import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Dialog, DialogFooter, PrimaryButton, DefaultButton} from '@fluentui/react';
import { DialogType } from '@fluentui/react/lib/Dialog';
import { useDispatch, useSelector } from 'react-redux';
import { Stack, SearchBox } from '@fluentui/react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { getNotifications, removeNotification, updateNotificationStatus } from 'store/actions/notificationActions';
import GeneralDetails from 'pages/general/details/GeneralDetails';
import { Icon } from '@fluentui/react';
import { getUserId } from 'utils/authUtils';

const NotificationsPage = () => {

    const currentUserId = getUserId();
    const dispatch = useDispatch();

    const [sortField, setSortField] = useState("createdOn");
    const [sortOrder, setSortOrder] = useState("desc");

    const [searchValue, setSearchValue] = useState('');
    const [searchText, setSearchText] = useState('');

    const [pageIndex, setPageIndex] = useState(0);
    const [allNotifications, setAllNotifications] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const scrollContainerRef = useRef(null);
    const [isGeneralDetailsOpen, setIsGeneralDetailsOpen] = useState(false);
    const [selectedWorkItemId, setSelectedWorkItemId] = useState(null);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [notificationToDelete, setNotificationToDelete] = useState(null);

    const { notifications = [], loading } = useSelector(state => state.notification || { notifications: [], loading: false });

    const pageSize = 50;

    const handleRefresh = () => {
        setSearchText('');
        setSearchValue('');
        setPageIndex(0);
        setAllNotifications([]);
        setHasMore(true);
        dispatch(getNotifications({
            sort: sortField,
            order: sortOrder,
            pageIndex: 0,
            pageSize,
        }));
    };

    useEffect(() => {
        setPageIndex(0);
        if (!allNotifications.length) {
            setAllNotifications([]);
        }
        setHasMore(true);

        const params = {
            sort: sortField,
            order: sortOrder,
            filterKey: searchText,
            pageIndex: 0,
            pageSize: allNotifications.length || pageSize
        };

        dispatch(getNotifications(params));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, searchText, sortField, sortOrder]);

    // Accumulate notifications when new data arrives
    useEffect(() => {
        if (notifications && Array.isArray(notifications)) {
            const isRefresh = pageIndex === 0 || notifications.length >= allNotifications.length;

            if (isRefresh) {
                setAllNotifications(notifications);
            } else {
                setAllNotifications([...allNotifications, ...notifications]);
            }

            setHasMore(notifications.length === pageSize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications, pageIndex, pageSize]);

    // Load more notifications
    const loadMoreNotifications = useCallback(() => {
        if (!loading && hasMore) {
            const nextPage = pageIndex + 1;
            setPageIndex(nextPage);

            const params = {
                sort: sortField,
                order: sortOrder,
                filterKey: searchText,
                pageIndex: nextPage,
                pageSize: pageSize
            };

            dispatch(getNotifications(params));
        }
    }, [loading, hasMore, pageIndex, dispatch, sortField, sortOrder, searchText, pageSize]);

    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;

        if (scrollHeight - scrollTop <= clientHeight * 1.2) {
            loadMoreNotifications();
        }
    }, [loadMoreNotifications]);

    const handleSort = (field) => {
        let newSortOrder = "asc";
        if (sortField === field) {
            newSortOrder = sortOrder === "asc" ? "desc" : "asc";
        }

        setSortField(field);
        setSortOrder(newSortOrder);
    };

    const handleRowClick = (notification) => {
        const matchingReceiver = notification.receivers?.find(
            r => r.receiverAddId === currentUserId
        );

        if (isReceiverUnread(notification) && matchingReceiver) {
            const payload = {
                ids: [notification.id],
                receivers: [matchingReceiver],
                status: notification.status
            };

            dispatch(updateNotificationStatus(payload, { silent: true }));
        }

        const claim = notification.claims?.find(
            (c) => c.claimType === "WorkItemId"
        );
        const workItemId = claim?.claimValue;
        setSelectedWorkItemId(workItemId);
        setIsGeneralDetailsOpen(true);
    };

    const closeDrawer = () => {
        setIsGeneralDetailsOpen(false);
        setSelectedWorkItemId(null);
        handleRefresh();
    };

    const isReceiverUnread = useCallback(
        (item) => {
            const receivers = item?.receivers;
            if (!receivers) return false;

            return receivers.some(
                (r) => r.receiverAddId === currentUserId && r.status === 2
            );
        },
        [currentUserId]
    );

    return (
        <div className="teams-card">
            <div className='page-action-header'>
                <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: "100%", display: "flex", alignItems: "center" } }}>
                    <Stack horizontal styles={{ root: { position: 'relative' } }}>
                        <SearchBox
                            style={{ width: '300px' }}
                            placeholder="Search here"
                            value={searchValue}
                            onChange={(_, newValue) => {
                                const limited = (newValue ?? '').slice(0, 40);
                                setSearchValue(limited);
                                if ((newValue ?? '').trim() === '') {
                                    setSearchValue('');
                                    handleRefresh();
                                }
                            }}
                            onSearch={newValue => setSearchText(newValue || '')}
                            showIcon={true}
                            disableAnimation={true}
                            styles={{
                                clearButton: { display: 'none' }
                            }}
                        />
                    </Stack>
                </Stack>
            </div>

            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="table-container"
            >
                <table>
                    <thead>
                        <tr>
                            <th className="w-70 cursor-pointer">
                                <div className="d-flex" onClick={() => handleSort("message")}>
                                    <span>Title {sortField === "message" && (sortOrder === "asc" ? "↑" : "↓")}</span>
                                </div>
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("createdOn")}>
                                Created On {sortField === "createdOn" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {allNotifications && allNotifications.length > 0 ? (
                            allNotifications.map((notification) => {
                                const notificationId = notification.id;
                                return (
                                    <tr key={notificationId}
                                        onClick={(e) => { handleRowClick(notification) }}>
                                        <td>
                                            <div>
                                                <div style={{ fontWeight: isReceiverUnread(notification) ? "bold" : "normal" }}>
                                                    {notification.message}.
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: isReceiverUnread(notification) ? "bold" : "normal" }}>
                                                {new Date(notification.createdOn).toLocaleString("en-US", {
                                                    year: "numeric",
                                                    month: "2-digit",
                                                    day: "2-digit",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                    hour12: false,
                                                })}
                                            </div>
                                        </td>
                                            <td><Icon
                                            iconName="Delete"
                                            onClick={(e) => {
                                                e.stopPropagation(); // prevent row click
                                                setNotificationToDelete(notification);
                                                setIsDeleteDialogOpen(true);
                                            }}
                                            style={{ cursor: "pointer" }}
                                        /></td>
                                    </tr>
                                );
                            })
                        ) : (
                            !loading && allNotifications.length === 0 && (
                                <tr>
                                    <td colSpan="2" className="text-center">
                                        No notifications found.
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>

                {isGeneralDetailsOpen && (
                    <GeneralDetails
                        closeDrawer={closeDrawer}
                        id={selectedWorkItemId}
                    />
                )}

                {isDeleteDialogOpen && (
                    <Dialog
                        hidden={!isDeleteDialogOpen}
                        onDismiss={() => setIsDeleteDialogOpen(false)}
                        dialogContentProps={{
                            type: DialogType.normal,
                            title: "Delete Notification",
                            subText: "Are you sure you want to delete this notification?"
                        }}
                        modalProps={{
                            isBlocking: true
                        }}
                    >
                        <DialogFooter>
                            <DefaultButton
                                text="No"
                                onClick={() => setIsDeleteDialogOpen(false)}
                            />
                            <PrimaryButton
                                text="Yes"
                                onClick={() => {
                                    const currentReceiver = notificationToDelete.receivers?.find(
                                        r => r.receiverAddId === currentUserId
                                    );

                                    const body = {
                                        id: notificationToDelete.id,
                                        receiverId: currentReceiver?.receiverId
                                    };

                                    dispatch(removeNotification(body)).then(() => {
                                        handleRefresh();
                                    });
                                    setIsDeleteDialogOpen(false);
                                    setNotificationToDelete(null);
                                }}
                            />
                        </DialogFooter>
                    </Dialog>
                )}

                {/* Loading indicator for subsequent pages */}
                {loading && pageIndex > 0 && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spinner size={SpinnerSize.medium} label="Loading more notifications..." />
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsPage;
